import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from '../lib/utils';
import { usePageTitle } from '../hooks/usePageTitle';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Checkbox } from '../components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { Plus, MoreHorizontal, Pencil, Trash2, FileText, Search, Loader2, Eye, Copy } from 'lucide-react';

// Default field position
const defaultFieldPosition = {
  x: 0,
  y: 0,
  font_size: 12,
  font_weight: 'normal',
  text_align: 'left',
  enabled: true
};

// Available fields for certificate
const CERTIFICATE_FIELDS = [
  { key: 'certificate_number', label: 'Certificate Number', defaultY: 50 },
  { key: 'student_name', label: 'Student Name', defaultY: 80 },
  { key: 'course_name', label: 'Course Name', defaultY: 110 },
  { key: 'company_name', label: 'Company Name', defaultY: 140 },
  { key: 'issue_date', label: 'Issue Date', defaultY: 170 },
  { key: 'expiry_date', label: 'Expiry Date', defaultY: 200 },
  { key: 'instructor_names', label: 'Instructor Name(s)', defaultY: 230 },
  { key: 'instructor_numbers', label: 'Instructor Number(s)', defaultY: 260 },
  { key: 'training_center_name', label: 'Training Center', defaultY: 290 },
  { key: 'duration_hours', label: 'Duration (Hours)', defaultY: 320 },
  { key: 'declaration_text', label: 'Declaration Text', defaultY: 350 },
  { key: 'extra_text', label: 'Extra Text', defaultY: 380 },
  { key: 'signature', label: 'Signature Image', defaultY: 410, isImage: true },
];

const CertificateTemplates = () => {
  usePageTitle('Certificate Templates');
  const [templates, setTemplates] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    page_orientation: 'portrait',
    fields: {}
  });

  // Initialize fields with defaults
  const initializeFields = () => {
    const fields = {};
    CERTIFICATE_FIELDS.forEach(field => {
      fields[field.key] = {
        ...defaultFieldPosition,
        x: 20,
        y: field.defaultY,
        enabled: false
      };
    });
    return fields;
  };

  const fetchData = async () => {
    try {
      const [templatesRes, coursesRes] = await Promise.all([
        axios.get(`${API_URL}/certificate-templates`),
        axios.get(`${API_URL}/courses`)
      ]);
      setTemplates(templatesRes.data);
      setCourses(coursesRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      page_orientation: 'portrait',
      fields: initializeFields()
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name) {
      toast.error('Please enter a template name');
      return;
    }

    setSaving(true);
    
    try {
      // Build payload with field positions
      const payload = {
        name: formData.name,
        description: formData.description || null,
        page_orientation: formData.page_orientation
      };

      // Add each field's position - always send the field to allow disabling
      CERTIFICATE_FIELDS.forEach(field => {
        const fieldData = formData.fields[field.key];
        if (fieldData && fieldData.enabled) {
          payload[field.key] = {
            x: parseFloat(fieldData.x) || 0,
            y: parseFloat(fieldData.y) || 0,
            font_size: parseInt(fieldData.font_size) || 12,
            font_weight: fieldData.font_weight || 'normal',
            text_align: fieldData.text_align || 'left',
            enabled: true
          };
        } else {
          // Send disabled field with enabled: false so backend can update it
          payload[field.key] = {
            x: parseFloat(fieldData?.x) || 0,
            y: parseFloat(fieldData?.y) || 0,
            font_size: parseInt(fieldData?.font_size) || 12,
            font_weight: fieldData?.font_weight || 'normal',
            text_align: fieldData?.text_align || 'left',
            enabled: false
          };
        }
      });
      
      if (editingTemplate) {
        await axios.put(`${API_URL}/certificate-templates/${editingTemplate.id}`, payload);
        toast.success('Template updated successfully');
      } else {
        await axios.post(`${API_URL}/certificate-templates`, payload);
        toast.success('Template created successfully');
      }
      
      setDialogOpen(false);
      setEditingTemplate(null);
      resetForm();
      fetchData();
    } catch (error) {
      const errorDetail = error.response?.data?.detail;
      toast.error(typeof errorDetail === 'string' ? errorDetail : 'Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (template) => {
    setEditingTemplate(template);
    
    // Build fields from template data
    const fields = initializeFields();
    CERTIFICATE_FIELDS.forEach(field => {
      if (template[field.key]) {
        fields[field.key] = {
          ...template[field.key],
          // Respect the actual enabled value from the database
          enabled: template[field.key].enabled !== false
        };
      }
    });
    
    setFormData({
      name: template.name,
      description: template.description || '',
      page_orientation: template.page_orientation || 'portrait',
      fields
    });
    setDialogOpen(true);
  };

  const handleDuplicate = async (template) => {
    try {
      // Create a copy of the template with a new name
      const duplicateData = {
        name: `${template.name} (Copy)`,
        description: template.description,
        page_orientation: template.page_orientation,
        certificate_number: template.certificate_number,
        student_name: template.student_name,
        course_name: template.course_name,
        company_name: template.company_name,
        issue_date: template.issue_date,
        expiry_date: template.expiry_date,
        instructor_names: template.instructor_names,
        instructor_numbers: template.instructor_numbers,
        training_center_name: template.training_center_name,
        duration_hours: template.duration_hours,
        declaration_text: template.declaration_text,
        extra_text: template.extra_text,
        signature: template.signature
      };
      
      await axios.post(`${API_URL}/certificate-templates`, duplicateData);
      toast.success('Template duplicated successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to duplicate template');
    }
  };

  const handleDelete = async (template) => {
    setDeleteConfirm(template);
  };

  const confirmDelete = async () => {
    if (!deleteConfirm) return;
    
    const template = deleteConfirm;
    setDeleteConfirm(null);
    
    try {
      await axios.delete(`${API_URL}/certificate-templates/${template.id}`);
      toast.success('Template deleted successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete template');
    }
  };

  const handlePreview = (template) => {
    setEditingTemplate(template);
    setPreviewOpen(true);
  };

  const updateFieldPosition = (fieldKey, property, value) => {
    setFormData(prev => ({
      ...prev,
      fields: {
        ...prev.fields,
        [fieldKey]: {
          ...prev.fields[fieldKey],
          [property]: value
        }
      }
    }));
  };

  const getCoursesUsingTemplate = (templateId) => {
    return courses.filter(c => c.certificate_template_id === templateId);
  };

  const filteredTemplates = templates.filter(template =>
    template.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (template.description || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-8 bg-slate-200 rounded w-48" />
        <div className="h-96 bg-slate-200 rounded-xl" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="certificate-templates-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Certificate Templates
          </h1>
          <p className="text-slate-500 mt-1">
            Configure print layouts for different certificate types
          </p>
        </div>
        <Button 
          className="bg-[#2C9DE1] hover:bg-[#1A7BB8] text-white"
          onClick={() => {
            setEditingTemplate(null);
            resetForm();
            setFormData(prev => ({ ...prev, fields: initializeFields() }));
            setDialogOpen(true);
          }}
          data-testid="create-template-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          New Template
        </Button>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
        <Input
          placeholder="Search templates..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
          data-testid="search-templates"
        />
      </div>

      {/* Templates List */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg font-semibold">
            Templates ({filteredTemplates.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Orientation</TableHead>
                <TableHead>Fields Configured</TableHead>
                <TableHead>Used By</TableHead>
                <TableHead className="w-[80px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredTemplates.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-slate-500">
                    No templates found. Create your first certificate template.
                  </TableCell>
                </TableRow>
              ) : (
                filteredTemplates.map((template) => {
                  const coursesUsing = getCoursesUsingTemplate(template.id);
                  const enabledFields = CERTIFICATE_FIELDS.filter(f => template[f.key]?.enabled).length;
                  
                  return (
                    <TableRow key={template.id}>
                      <TableCell className="font-medium">{template.name}</TableCell>
                      <TableCell className="text-slate-500 max-w-[200px] truncate">
                        {template.description || '-'}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="capitalize">
                          {template.page_orientation}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary">
                          {enabledFields} / {CERTIFICATE_FIELDS.length}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {coursesUsing.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {coursesUsing.slice(0, 2).map(course => (
                              <Badge key={course.id} className="bg-emerald-100 text-emerald-800 text-xs">
                                {course.name}
                              </Badge>
                            ))}
                            {coursesUsing.length > 2 && (
                              <Badge variant="secondary" className="text-xs">
                                +{coursesUsing.length - 2} more
                              </Badge>
                            )}
                          </div>
                        ) : (
                          <span className="text-slate-400 text-sm">Not assigned</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handlePreview(template)}>
                              <Eye className="w-4 h-4 mr-2" />
                              Preview
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleEdit(template)}>
                              <Pencil className="w-4 h-4 mr-2" />
                              Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleDuplicate(template)}>
                              <Copy className="w-4 h-4 mr-2" />
                              Duplicate
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              className="text-red-600"
                              onClick={() => handleDelete(template)}
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
              {editingTemplate ? 'Edit Certificate Template' : 'Create Certificate Template'}
            </DialogTitle>
            <DialogDescription>
              Configure the print positions for each field on your pre-printed certificate (A4 size: 210mm × 297mm)
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Template Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Emergency First Aid Certificate"
                  data-testid="template-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="orientation">Page Orientation</Label>
                <Select 
                  value={formData.page_orientation} 
                  onValueChange={(value) => setFormData({ ...formData, page_orientation: value })}
                >
                  <SelectTrigger data-testid="orientation-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="portrait">Portrait (210mm × 297mm)</SelectItem>
                    <SelectItem value="landscape">Landscape (297mm × 210mm)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Input
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Optional description"
              />
            </div>

            {/* Field Positions */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label className="text-base font-semibold">Field Positions</Label>
                <p className="text-xs text-slate-500">
                  Position values in millimeters from top-left corner
                </p>
              </div>
              
              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="w-[50px]">Enable</TableHead>
                      <TableHead>Field</TableHead>
                      <TableHead className="w-[80px]">X (mm)</TableHead>
                      <TableHead className="w-[80px]">Y (mm)</TableHead>
                      <TableHead className="w-[80px]">Font Size</TableHead>
                      <TableHead className="w-[100px]">Weight</TableHead>
                      <TableHead className="w-[100px]">Align</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {CERTIFICATE_FIELDS.map((field) => {
                      const fieldData = formData.fields[field.key] || defaultFieldPosition;
                      return (
                        <TableRow key={field.key} className={fieldData.enabled ? 'bg-emerald-50/50' : ''}>
                          <TableCell>
                            <Checkbox
                              checked={fieldData.enabled}
                              onCheckedChange={(checked) => updateFieldPosition(field.key, 'enabled', checked)}
                              data-testid={`enable-${field.key}`}
                            />
                          </TableCell>
                          <TableCell className="font-medium">{field.label}</TableCell>
                          <TableCell>
                            <Input
                              type="number"
                              min="0"
                              max="297"
                              step="1"
                              value={fieldData.x}
                              onChange={(e) => updateFieldPosition(field.key, 'x', e.target.value)}
                              disabled={!fieldData.enabled}
                              className="h-8 w-full"
                            />
                          </TableCell>
                          <TableCell>
                            <Input
                              type="number"
                              min="0"
                              max="297"
                              step="1"
                              value={fieldData.y}
                              onChange={(e) => updateFieldPosition(field.key, 'y', e.target.value)}
                              disabled={!fieldData.enabled}
                              className="h-8 w-full"
                            />
                          </TableCell>
                          <TableCell>
                            <Input
                              type="number"
                              min="6"
                              max="72"
                              value={fieldData.font_size}
                              onChange={(e) => updateFieldPosition(field.key, 'font_size', e.target.value)}
                              disabled={!fieldData.enabled}
                              className="h-8 w-full"
                            />
                          </TableCell>
                          <TableCell>
                            <Select
                              value={fieldData.font_weight}
                              onValueChange={(value) => updateFieldPosition(field.key, 'font_weight', value)}
                              disabled={!fieldData.enabled}
                            >
                              <SelectTrigger className="h-8">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="normal">Normal</SelectItem>
                                <SelectItem value="bold">Bold</SelectItem>
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell>
                            <Select
                              value={fieldData.text_align}
                              onValueChange={(value) => updateFieldPosition(field.key, 'text_align', value)}
                              disabled={!fieldData.enabled}
                            >
                              <SelectTrigger className="h-8">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="left">Left</SelectItem>
                                <SelectItem value="center">Center</SelectItem>
                                <SelectItem value="right">Right</SelectItem>
                              </SelectContent>
                            </Select>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                className="bg-[#2C9DE1] hover:bg-[#1A7BB8] text-white"
                disabled={saving}
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  editingTemplate ? 'Update Template' : 'Create Template'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
              Template Preview: {editingTemplate?.name}
            </DialogTitle>
            <DialogDescription>
              Preview of field positions on {editingTemplate?.page_orientation === 'landscape' ? 'landscape' : 'portrait'} A4
            </DialogDescription>
          </DialogHeader>
          
          {editingTemplate && (
            <div className="flex justify-center">
              <div 
                className="border-2 border-dashed border-slate-300 bg-white relative"
                style={{
                  width: editingTemplate.page_orientation === 'landscape' ? '400px' : '280px',
                  height: editingTemplate.page_orientation === 'landscape' ? '280px' : '400px',
                }}
              >
                {/* A4 label */}
                <div className="absolute top-2 right-2 text-xs text-slate-400">
                  A4 {editingTemplate.page_orientation}
                </div>
                
                {/* Render field positions */}
                {CERTIFICATE_FIELDS.map((field) => {
                  const fieldData = editingTemplate[field.key];
                  if (!fieldData?.enabled) return null;
                  
                  // Scale from mm to preview pixels (roughly 1.33 for portrait width)
                  const scale = editingTemplate.page_orientation === 'landscape' 
                    ? 400 / 297 
                    : 280 / 210;
                  
                  return (
                    <div
                      key={field.key}
                      className="absolute px-1 py-0.5 bg-blue-100 border border-blue-300 rounded text-xs truncate max-w-[150px]"
                      style={{
                        left: `${fieldData.x * scale}px`,
                        top: `${fieldData.y * scale}px`,
                        fontSize: `${Math.max(8, fieldData.font_size * 0.6)}px`,
                        fontWeight: fieldData.font_weight,
                        textAlign: fieldData.text_align,
                      }}
                      title={`${field.label}: X=${fieldData.x}mm, Y=${fieldData.y}mm`}
                    >
                      {field.label}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setPreviewOpen(false)}>
              Close
            </Button>
            <Button 
              className="bg-[#2C9DE1] hover:bg-[#1A7BB8] text-white"
              onClick={() => {
                setPreviewOpen(false);
                handleEdit(editingTemplate);
              }}
            >
              <Pencil className="w-4 h-4 mr-2" />
              Edit Template
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      {deleteConfirm && (
        <Dialog open={true} onOpenChange={() => setDeleteConfirm(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
                Delete Template
              </DialogTitle>
              <DialogDescription>
                Are you sure you want to delete <strong>{deleteConfirm.name}</strong>? This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="flex gap-2 mt-4">
              <Button
                variant="outline"
                className="bg-[#2C9DE1] hover:bg-[#1A7BB8] text-white border-0"
                onClick={() => setDeleteConfirm(null)}
              >
                Cancel
              </Button>
              <Button
                className="bg-red-600 hover:bg-red-700 text-white"
                onClick={confirmDelete}
              >
                Delete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default CertificateTemplates;
